import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm({
      company_name: r.data.company_name || "",
      logo_url: r.data.logo_url || "",
      default_delivery_type: r.data.default_delivery_type || "Normal",
      default_order_status: r.data.default_order_status || "Picked Up",
    })).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required."); return; }
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Settings saved.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="settings-title">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Company branding and order defaults.</p>
      </div>

      {!form ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-6" data-testid="settings-form">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Company Name</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="settings-company-name" className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Company Logo URL</Label>
            <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="settings-logo-url" className="border-slate-300 rounded-sm" />
            {form.logo_url && (
              <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-sm inline-block">
                <img src={form.logo_url} alt="Logo preview" className="h-10 w-auto" data-testid="settings-logo-preview" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Default Delivery Type</Label>
              <Select value={form.default_delivery_type} onValueChange={(v) => setForm({ ...form, default_delivery_type: v })}>
                <SelectTrigger data-testid="settings-default-type" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Normal">Normal</SelectItem><SelectItem value="Express">Express</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Default Order Status</Label>
              <Select value={form.default_order_status} onValueChange={(v) => setForm({ ...form, default_order_status: v })}>
                <SelectTrigger data-testid="settings-default-status" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Picked Up", "In Transit", "Delivered", "Returned to Client"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">New orders always start as "Picked Up" per business rules.</p>
            </div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="settings-save-button" className="bg-primary hover:bg-[#152D53] rounded-sm px-6">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
          </Button>
        </div>
      )}
    </div>
  );
}
